import React from 'react';

/**
 * Authentic woven Berber rug runner border (Tapis Berbère / Asalas Amazigh)
 * Combines diamond motifs (losanges imbriqués / tit n tsekurt),
 * chevron bands, and earthy terracotta, saffron gold, and Mediterranean indigo accents.
 */
export const BerberRugBorder: React.FC<{ className?: string; inverted?: boolean }> = React.memo(({ className = '', inverted = false }) => (
  <div className={`w-full h-3 sm:h-3.5 flex items-center overflow-hidden z-20 pointer-events-none select-none bg-[#fef3c7]/95 ${inverted ? 'border-t' : 'border-b'} border-amber-800/30 ${className}`}>
    <div className="flex w-full h-full justify-between items-center opacity-95">
      {Array.from({ length: 60 }).map((_, i) => (
        <svg key={i} className="h-full w-4 sm:w-5 flex-shrink-0" viewBox="0 0 20 14" preserveAspectRatio="none">
          {/* Base woven background */}
          <rect x="0" y="0" width="20" height="14" fill={i % 2 === 0 ? '#fef3c7' : '#fde68a'} />
          {/* Top and bottom thread stitches */}
          <line x1="0" y1="1" x2="20" y2="1" stroke="#92400e" strokeWidth="1" strokeDasharray="2,2" />
          <line x1="0" y1="13" x2="20" y2="13" stroke="#92400e" strokeWidth="1" strokeDasharray="2,2" />
          
          {/* Outer Berber Diamond (Terracotta / Crimson / Saffron) */}
          <polygon
            points="10,2 18,7 10,12 2,7"
            fill={i % 3 === 0 ? '#b91c1c' : i % 3 === 1 ? '#c2410c' : '#1e3a8a'}
          />
          {/* Inner Golden Diamond / Medallion */}
          <polygon
            points="10,4 15,7 10,10 5,7"
            fill={i % 2 === 0 ? '#fbbf24' : '#f59e0b'}
          />
          {/* Center Core Dot / Cross */}
          <circle cx="10" cy="7" r="1.2" fill="#292524" />
          
          {/* Small side triangles (sawtooth carpet fringe) */}
          <polygon points="0,4 3,7 0,10" fill="#92400e" opacity="0.6" />
          <polygon points="20,4 17,7 20,10" fill="#92400e" opacity="0.6" />
        </svg>
      ))}
    </div>
  </div>
));

/**
 * Elegant Berber decorative horizontal divider with a central Amazigh Tifinagh Yaz 'ⵣ'
 */
export const BerberRugDivider: React.FC<{ label?: string; className?: string }> = React.memo(({ label, className = '' }) => (
  <div className={`flex items-center justify-center gap-2 py-2 w-full select-none ${className}`}>
    <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-amber-700/40 to-amber-900/60" />
    <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100/90 border border-amber-800/30 text-amber-950 text-[11px] font-cinzel font-bold tracking-widest shadow-xs">
      <span className="text-red-700 text-xs font-black">ⵣ</span>
      {label && <span className="uppercase text-[10px] sm:text-[11px] tracking-wider text-stone-800 font-bold">{label}</span>}
      <span className="text-red-700 text-xs font-black">ⵣ</span>
    </div>
    <div className="flex-1 h-[2px] bg-gradient-to-l from-transparent via-amber-700/40 to-amber-900/60" />
  </div>
));

export const NpcSprite: React.FC<{ type: string; isWalking?: boolean; flip?: boolean }> = React.memo(({ type, isWalking = false, flip = false }) => {
  return (
    <div className={`relative w-10 h-14 z-30 transition-all duration-700 ${flip ? '-scale-x-100' : ''}`}>
      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-2 bg-black/50 rounded-full blur-[1px]"></div>
      <svg className={`w-full h-full drop-shadow-md ${isWalking ? 'animate-[bounce_0.35s_infinite]' : ''}`} viewBox="0 0 32 40">
        {/* Legs helper for all standard humanoids */}
        {type !== 'spirit' && (
          <>
            <rect x="11" y="28" width="4" height="8" fill="#1e293b" />
            <rect x="17" y="28" width="4" height="8" fill="#1e293b" />
            <rect x="10" y="36" width="5" height="4" fill="#0f172a" />
            <rect x="17" y="36" width="5" height="4" fill="#0f172a" />
          </>
        )}

        {type === 'old_man' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <rect x="8" y="6" width="16" height="4" fill="#f8fafc" />
            <circle cx="13" cy="12" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="12" r="1.5" fill="#1e293b" />
            <path d="M 10,16 Q 16,22 22,16 Z" fill="#e2e8f0" />
            {/* Tunic/Body */}
            <path d="M 6,18 L 26,18 L 28,30 L 4,30 Z" fill="#78350f" />
            <line x1="26" y1="14" x2="26" y2="36" stroke="#4a2e15" strokeWidth="2" />
          </>
        )}
        {type === 'witch' && (
          <>
            <rect x="10" y="10" width="12" height="8" fill="#fcd34d" />
            <path d="M 6,18 L 26,18 L 28,34 L 4,34 Z" fill="#4c1d95" />
            <path d="M 10,18 L 22,18 L 24,34 L 8,34 Z" fill="#7e22ce" opacity="0.5" />
            <circle cx="13" cy="14" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="14" r="1.5" fill="#1e293b" />
            {/* Witch Hat */}
            <path d="M 2,10 L 30,10 L 16,-4 Z" fill="#3b0764" />
            <path d="M 2,10 L 30,10 L 30,12 L 2,12 Z" fill="#2e054e" />
          </>
        )}
        {type === 'old_woman' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 6,6 L 26,6 L 24,14 L 8,14 Z" fill="#facc15" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <path d="M 13,16 Q 16,18 19,16" fill="none" stroke="#b45309" strokeWidth="1" />
            {/* Tunic/Body */}
            <path d="M 8,18 L 24,18 L 26,32 L 6,32 Z" fill="#ef4444" />
            <path d="M 12,18 L 20,18 L 22,32 L 10,32 Z" fill="#facc15" opacity="0.3" />
          </>
        )}
        {type === 'poet' && (
          <>
            <rect x="10" y="10" width="12" height="8" fill="#fcd34d" />
            <path d="M 8,4 Q 16,0 24,4 L 26,12 Q 16,8 6,12 Z" fill="#1e293b" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <path d="M 14,16 Q 16,17 18,16" fill="none" stroke="#b45309" strokeWidth="1" />
            {/* Tunic/Body */}
            <path d="M 10,18 L 22,18 L 26,30 L 6,30 Z" fill="#9333ea" />
            <circle cx="16" cy="24" r="3" fill="#d8b4fe" />
          </>
        )}
        {type === 'hermit' && (
          <>
            <rect x="12" y="12" width="8" height="6" fill="#fcd34d" />
            <path d="M 8,4 Q 16,0 24,4 L 26,16 L 22,12 L 10,12 L 6,16 Z" fill="#64748b" />
            <circle cx="14" cy="14" r="1" fill="#1e293b" />
            <circle cx="18" cy="14" r="1" fill="#1e293b" />
            <rect x="12" y="18" width="8" height="12" fill="#f1f5f9" />
            <path d="M 6,18 L 26,18 L 28,30 L 4,30 Z" fill="#94a3b8" />
            <line x1="26" y1="10" x2="26" y2="36" stroke="#4a2e15" strokeWidth="2" />
          </>
        )}
        {type === 'spirit' && (
          <>
            <ellipse cx="16" cy="20" rx="12" ry="18" fill="#22d3ee" opacity="0.4" />
            <ellipse cx="16" cy="18" rx="8" ry="12" fill="#67e8f9" opacity="0.6" />
            <circle cx="13" cy="16" r="2" fill="#fff" />
            <circle cx="19" cy="16" r="2" fill="#fff" />
            <path d="M 12,22 Q 16,24 20,22" fill="none" stroke="#fff" strokeWidth="1.5" />
            <path d="M 8,30 Q 16,45 24,30 Z" fill="#22d3ee" opacity="0.5" />
          </>
        )}
        {type === 'farmer' && (
          <>
            <rect x="10" y="10" width="12" height="8" fill="#fcd34d" />
            <ellipse cx="16" cy="8" rx="10" ry="4" fill="#fef08a" stroke="#ca8a04" strokeWidth="1" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <rect x="12" y="16" width="8" height="2" fill="#d97706" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#166534" />
            <line x1="28" y1="10" x2="28" y2="36" stroke="#a16207" strokeWidth="2" />
            <line x1="26" y1="8" x2="28" y2="12" stroke="#a16207" strokeWidth="1.5" />
            <line x1="30" y1="8" x2="28" y2="12" stroke="#a16207" strokeWidth="1.5" />
          </>
        )}
        {(type === 'shepherd' || type === 'berger') && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <ellipse cx="16" cy="6" rx="8" ry="3" fill="#78350f" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#a16207" />
            <line x1="27" y1="8" x2="27" y2="36" stroke="#451a03" strokeWidth="2" />
            <path d="M 27,8 Q 30,4 32,8" fill="none" stroke="#451a03" strokeWidth="2" />
          </>
        )}
        {type === 'artisan' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <rect x="8" y="6" width="16" height="4" fill="#ea580c" />
            <circle cx="13" cy="12" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="12" r="1.5" fill="#1e293b" />
            <rect x="11" y="15" width="10" height="3" fill="#1e293b" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#b45309" />
            <rect x="6" y="24" width="20" height="4" fill="#78350f" />
            <rect x="25" y="22" width="6" height="4" fill="#94a3b8" />
            <rect x="27" y="26" width="2" height="6" fill="#4a2e15" />
          </>
        )}
        {type === 'peasant' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <rect x="10" y="4" width="12" height="4" fill="#3b82f6" />
            <rect x="6" y="8" width="8" height="2" fill="#3b82f6" />
            <circle cx="13" cy="12" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="12" r="1.5" fill="#1e293b" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#475569" />
            <circle cx="26" cy="24" r="5" fill="#a3e635" stroke="#4d7c0f" strokeWidth="1" />
          </>
        )}
        {type === 'merchant' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 6,8 Q 16,0 26,8 L 26,10 L 6,10 Z" fill="#14b8a6" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <polygon points="16,16 14,20 18,20" fill="#1e293b" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#ca8a04" />
            <rect x="22" y="22" width="8" height="8" fill="#78350f" rx="2" />
            <circle cx="26" cy="26" r="2" fill="#fef08a" />
          </>
        )}
        {type === 'doctor' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 8,6 L 24,6 L 22,10 L 10,10 Z" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <rect x="10" y="12" width="12" height="1" fill="#475569" />
            <circle cx="13" cy="13" r="2.5" fill="none" stroke="#475569" strokeWidth="1" />
            <circle cx="19" cy="13" r="2.5" fill="none" stroke="#475569" strokeWidth="1" />
            <path d="M 12,17 Q 16,22 20,17 Z" fill="#cbd5e1" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#f8fafc" />
            <rect x="22" y="24" width="8" height="8" fill="#ef4444" rx="1" />
            <rect x="25" y="26" width="2" height="4" fill="#fff" />
            <rect x="24" y="27" width="4" height="2" fill="#fff" />
          </>
        )}
        {type === 'hunter' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 6,8 Q 16,0 26,8 L 24,12 L 8,12 Z" fill="#3f3f46" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <rect x="10" y="16" width="12" height="2" fill="#1e293b" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#3f6212" />
            <path d="M 28,10 Q 32,20 28,30" fill="none" stroke="#4a2e15" strokeWidth="2" />
            <line x1="28" y1="10" x2="28" y2="30" stroke="#cbd5e1" strokeWidth="0.5" />
          </>
        )}
        {type === 'seer' && (
          <>
            <rect x="10" y="10" width="12" height="8" fill="#fcd34d" />
            <path d="M 6,10 C 10,0 22,0 26,10 Z" fill="#000" />
            <rect x="6" y="8" width="20" height="3" fill="#c026d3" />
            <circle cx="16" cy="9.5" r="1.5" fill="#fef08a" />
            <circle cx="13" cy="14" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="14" r="1.5" fill="#1e293b" />
            <path d="M 10,18 L 22,18 L 26,32 L 6,32 Z" fill="#701a75" />
            <circle cx="26" cy="24" r="4" fill="#a855f7" opacity="0.8" />
            <circle cx="26" cy="24" r="2" fill="#fdf4ff" opacity="0.9" />
          </>
        )}
        {type === 'storyteller' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 6,6 L 26,6 L 24,14 L 8,14 Z" fill="#b91c1c" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <path d="M 13,16 Q 16,18 19,16" fill="none" stroke="#b45309" strokeWidth="1" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#0ea5e9" />
            <rect x="12" y="22" width="8" height="6" fill="#fef3c7" />
            <line x1="16" y1="22" x2="16" y2="28" stroke="#d97706" strokeWidth="1" />
          </>
        )}
        {type === 'weaver' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 6,6 L 26,6 L 24,14 L 8,14 Z" fill="#facc15" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <path d="M 8,18 L 24,18 L 26,32 L 6,32 Z" fill="#f8fafc" />
            <polygon points="12,24 16,20 20,24 16,28" fill="#ef4444" />
            <polygon points="12,30 16,26 20,30 16,34" fill="#eab308" />
            <line x1="26" y1="18" x2="26" y2="28" stroke="#78350f" strokeWidth="2" />
            <ellipse cx="26" cy="23" rx="3" ry="4" fill="#ef4444" />
          </>
        )}
        {type === 'judge' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 6,6 Q 16,-2 26,6 L 26,10 L 6,10 Z" fill="#166534" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <path d="M 10,16 Q 16,30 22,16 Z" fill="#f8fafc" />
            <path d="M 8,18 L 24,18 L 26,30 L 6,30 Z" fill="#1e3a8a" />
          </>
        )}
        {type === 'modern_boy' && (
          <>
            <rect x="10" y="8" width="12" height="10" fill="#fcd34d" />
            <path d="M 6,8 Q 16,0 26,8 L 26,12 L 6,10 Z" fill="#1e293b" />
            <path d="M 26,8 L 30,10 L 26,12 Z" fill="#ef4444" />
            <circle cx="13" cy="13" r="1.5" fill="#1e293b" />
            <circle cx="19" cy="13" r="1.5" fill="#1e293b" />
            <path d="M 8,18 L 24,18 L 25,24 L 7,24 Z" fill="#3b82f6" />
            <path d="M 10,19 L 22,19 L 21,23 L 11,23 Z" fill="#60a5fa" />
            <path d="M 9,24 L 23,24 L 24,30 L 8,30 Z" fill="#1e3a8a" />
            <rect x="22" y="20" width="4" height="6" fill="#111827" rx="1" />
            <rect x="23" y="21" width="2" height="4" fill="#38bdf8" />
          </>
        )}
      </svg>
    </div>
  );
});

export const PlayerSprite: React.FC<{ isWalking: boolean; isJumping: boolean; skin?: string }> = React.memo(({ 
  skin = 'default',
  isWalking,
  isJumping
}) => (
  <div
    className={`relative w-10 h-14 z-30 transition-transform duration-200 ${
      isJumping ? '-translate-y-6' : ''
    }`}
  >
    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-2 bg-black/50 rounded-full blur-[1px]"></div>
    <svg
      className={`w-full h-full ${isWalking ? 'animate-[bounce_0.3s_infinite]' : ''}`}
      viewBox="0 0 32 40"
    >
      {skin === 'default' && (
        <>
          <rect x="8" y="4" width="16" height="6" fill="#1e293b" />
          <rect x="6" y="8" width="20" height="3" fill="#1e293b" />
          <rect x="10" y="10" width="12" height="8" fill="#fcd34d" />
          <rect x="12" y="13" width="2" height="2" fill="#000" />
          <rect x="18" y="13" width="2" height="2" fill="#000" />
          <rect x="9" y="18" width="14" height="10" fill="#dc2626" />
          <rect x="14" y="18" width="4" height="10" fill="#f8fafc" />
          <rect x="4" y="20" width="5" height="6" fill="#ca8a04" />
          <rect x="11" y="28" width="4" height="8" fill="#334155" />
          <rect x="17" y="28" width="4" height="8" fill="#334155" />
          <rect x="10" y="36" width="5" height="4" fill="#1e293b" />
          <rect x="17" y="36" width="5" height="4" fill="#1e293b" />
        </>
      )}
      {skin === 'uccen' && (
        <>
          <polygon points="10,12 4,4 14,10" fill="#d97706" />
          <polygon points="22,12 28,4 18,10" fill="#d97706" />
          <path d="M 10,10 L 22,10 L 26,20 L 16,30 L 6,20 Z" fill="#b45309" />
          <rect x="12" y="15" width="2" height="2" fill="#fef08a" />
          <rect x="18" y="15" width="2" height="2" fill="#fef08a" />
          <polygon points="16,24 14,20 18,20" fill="#000" />
          <rect x="12" y="30" width="3" height="8" fill="#92400e" />
          <rect x="17" y="30" width="3" height="8" fill="#92400e" />
        </>
      )}
      {skin === 'teryel' && (
        <>
          <path d="M 6,10 C 6,2 26,2 26,10 L 24,18 L 8,18 Z" fill="#4c1d95" />
          <circle cx="16" cy="14" r="8" fill="#a78bfa" />
          <circle cx="13" cy="13" r="1.5" fill="#ef4444" />
          <circle cx="19" cy="13" r="1.5" fill="#ef4444" />
          <path d="M 13,17 C 15,19 17,19 19,17" fill="none" stroke="#581c87" strokeWidth="1" />
          <path d="M 8,22 L 24,22 L 28,36 L 4,36 Z" fill="#2e1065" />
          <polygon points="12,22 16,30 20,22" fill="#7c3aed" />
        </>
      )}
      {skin === 'ayyis' && (
        <>
          <rect x="12" y="8" width="8" height="14" fill="#78350f" rx="4" />
          <path d="M 16,4 Q 18,8 20,8 L 12,8 Q 14,8 16,4 Z" fill="#451a03" />
          <rect x="13" y="10" width="2" height="2" fill="#fff" />
          <rect x="17" y="10" width="2" height="2" fill="#fff" />
          <rect x="10" y="20" width="12" height="12" fill="#92400e" rx="2" />
          <rect x="11" y="32" width="3" height="8" fill="#451a03" />
          <rect x="18" y="32" width="3" height="8" fill="#451a03" />
          <path d="M 10,22 Q 4,24 6,34" fill="none" stroke="#451a03" strokeWidth="2" />
        </>
      )}
      {skin === 'igider' && (
        <>
          <circle cx="16" cy="12" r="6" fill="#fde047" />
          <polygon points="16,14 14,20 18,20" fill="#ea580c" />
          <circle cx="14" cy="11" r="1" fill="#000" />
          <circle cx="18" cy="11" r="1" fill="#000" />
          <path d="M 16,18 L 24,30 L 8,30 Z" fill="#854d0e" />
          <path d="M 8,24 Q 0,16 6,10 L 10,18 Z" fill="#a16207" />
          <path d="M 24,24 Q 32,16 26,10 L 22,18 Z" fill="#a16207" />
          <rect x="13" y="30" width="2" height="4" fill="#ea580c" />
          <rect x="17" y="30" width="2" height="4" fill="#ea580c" />
        </>
      )}
    </svg>
  </div>
));